import { AgeRange, Gender } from '@blisspointmedia/bpm-types/dist/YoutubePlanning';
import {
  CreatorSetAudienceAttribute,
  YouTubeCreatorSetMember
} from '@blisspointmedia/bpm-types/dist/pgTables/YoutubeCreatorSetMember';
import { AGE_KEYS, GENDER_KEYS } from '../youtube-planning-constants';
import { BuildRecommendationPrefill } from '../youtube-planning-types';
import { isAgeRangeArrConsecutive } from '../youtube-planning-utils';
import {
  AGE_SHARE_MIN,
  AudienceDimension,
  AudienceDimensionValue,
  CopyableRow,
  AttributeField,
  GENDER_DOMINANCE_MIN,
  REFERENCE_ROW_LIMIT,
  Weighting,
  WeightingValue
} from './creator-sets-constants';
import { parseCount, sharesViewerData } from './creator-sets-utils';

type AgeKey = keyof typeof AgeRange;
type GenderKey = keyof typeof Gender;

export interface AttributeShare {
  id: string;
  label: string;
  share: number;
}

export interface GenderSuggestion {
  key: GenderKey;
  // Male and female shares of the viewers whose gender Google knows, largest first.
  shares: AttributeShare[];
  // True when no gender reached GENDER_DOMINANCE_MIN and the suggestion fell back to All Genders.
  isFallback: boolean;
}

export interface AgeRangeSuggestion {
  // One contiguous span of buckets, or just UNSPECIFIED (All Ages) when it spans every bucket.
  keys: AgeKey[];
  // Share of the set's age-known viewers inside the suggested span.
  coveredShare: number;
  // Buckets under AGE_SHARE_MIN that the span takes in to stay contiguous.
  bridgedKeys: AgeKey[];
}

export interface CreatorSetTargeting {
  memberCount: number;
  // Members the aggregate draws on: those whose snapshot has audience data (creators who share
  // viewer data with Google) and, when weighting by subscribers, a subscriber count above 0.
  membersWithAudience: number;
  weighting: WeightingValue;
  // The top topic.
  interest?: AttributeShare;
  // Top affinity and in-market interests. Build Recommendation's Audience Interest search only
  // offers topics, so these can't be carried across.
  unavailableInterests: AttributeShare[];
  gender?: GenderSuggestion;
  ageRange?: AgeRangeSuggestion;
  devices: AttributeShare[];
  countries: AttributeShare[];
}

const SPAN_AGE_KEYS = AGE_KEYS.filter(key => key !== 'UNSPECIFIED');
const SPECIFIC_GENDER_KEYS = GENDER_KEYS.filter(key => key !== 'UNSPECIFIED');
const isSpanAgeKey = (id: string): id is AgeKey => SPAN_AGE_KEYS.some(key => key === id);
const isGenderKey = (id: string): id is GenderKey => SPECIFIC_GENDER_KEYS.some(key => key === id);

// The attribute's own id inside its dimension (an age bucket, a topic's machine id, …).
export const attributeId = ({ attribute, displayName }: CreatorSetAudienceAttribute): string =>
  attribute.ageRange?.type ??
  attribute.gender?.type ??
  attribute.device?.type ??
  attribute.location?.geoTargetConstant ??
  attribute.entity?.knowledgeGraphMachineId ??
  attribute.userInterest?.userInterestCategory ??
  displayName;

interface WeightedMember {
  weight: number;
  attributes: CreatorSetAudienceAttribute[];
}

// Members with audience data, weighted by subscriber count. If none has a positive subscriber
// count, every member counts equally.
const toWeightedMembers = (
  members: YouTubeCreatorSetMember[]
): { weighting: WeightingValue; weighted: WeightedMember[] } => {
  const withAudience = members.filter(sharesViewerData);
  const weighted = withAudience.map(({ insights }) => ({
    weight: Math.max(parseCount(insights.subscriber_count) ?? 0, 0),
    attributes: insights.channel_audience_attributes
  }));
  if (weighted.some(member => member.weight > 0)) {
    return { weighting: Weighting.SUBSCRIBERS, weighted };
  }
  return {
    weighting: Weighting.EQUAL,
    weighted: weighted.map(member => ({ ...member, weight: 1 }))
  };
};

// Each attribute's viewer share across the set: the weighted mean over members with audience
// data, counting a member without the attribute as 0. Largest first; ties keep first-seen order.
const aggregateDimension = (
  members: WeightedMember[],
  dimension: AudienceDimensionValue
): AttributeShare[] => {
  const totalWeight = members.reduce((sum, member) => sum + member.weight, 0);
  if (totalWeight === 0) return [];
  const byId = new Map<string, AttributeShare>();
  members.forEach(({ weight, attributes }) => {
    attributes
      .filter(attr => attr.dimension === dimension)
      .forEach(attr => {
        const id = attributeId(attr);
        const entry = byId.get(id) ?? { id, label: attr.displayName, share: 0 };
        entry.share += ((attr.viewerShare ?? 0) * weight) / totalWeight;
        byId.set(id, entry);
      });
  });
  return [...byId.values()].sort((a, b) => b.share - a.share);
};

// Shares of viewers whose gender Google knows, like age.
const suggestGender = (shares: AttributeShare[]): GenderSuggestion | undefined => {
  const specific = shares.filter(({ id }) => isGenderKey(id));
  const total = specific.reduce((sum, { share }) => sum + share, 0);
  if (total === 0) return undefined;
  const known = specific.map(entry => ({ ...entry, share: entry.share / total }));
  const [top] = known;
  if (top.share >= GENDER_DOMINANCE_MIN && isGenderKey(top.id)) {
    return { key: top.id, shares: known, isFallback: false };
  }
  return { key: 'UNSPECIFIED', shares: known, isFallback: true };
};

const suggestAgeRange = (shares: AttributeShare[]): AgeRangeSuggestion | undefined => {
  const shareByKey = new Map(shares.filter(({ id }) => isSpanAgeKey(id)).map(s => [s.id, s.share]));
  const total = [...shareByKey.values()].reduce((sum, share) => sum + share, 0);
  if (total === 0) return undefined;

  const qualifying = SPAN_AGE_KEYS.filter(
    key => (shareByKey.get(key) ?? 0) / total >= AGE_SHARE_MIN
  );
  if (qualifying.length === 0) return undefined;
  const first = SPAN_AGE_KEYS.indexOf(qualifying[0]);
  const last = SPAN_AGE_KEYS.indexOf(qualifying[qualifying.length - 1]);
  const span = SPAN_AGE_KEYS.slice(first, last + 1);
  const coveredShare = span.reduce((sum, key) => sum + (shareByKey.get(key) ?? 0), 0) / total;
  const bridgedKeys = isAgeRangeArrConsecutive(qualifying)
    ? []
    : span.filter(key => !qualifying.includes(key));

  // Every bucket in the span is what the form calls All Ages.
  const keys: AgeKey[] = span.length === SPAN_AGE_KEYS.length ? ['UNSPECIFIED'] : span;
  return { keys, coveredShare, bridgedKeys };
};

// What a set's audience suggests for Build Recommendation's Audience Interest, Gender and Age Range,
// as the form's own values. Other interests, devices and countries are reference only: the
// form has no field for devices or countries, and its Audience Interest search doesn't include
// affinity or in-market interests. Dimensions it doesn't recognize are ignored, and members without
// audience data are left out (see membersWithAudience).
export const aggregateCreatorSetTargeting = (
  members: YouTubeCreatorSetMember[]
): CreatorSetTargeting => {
  const { weighting, weighted } = toWeightedMembers(members);
  const [topInterest] = aggregateDimension(weighted, AudienceDimension.KNOWLEDGE_GRAPH);
  const unavailableInterests = [
    ...aggregateDimension(weighted, AudienceDimension.AFFINITY_USER_INTEREST),
    ...aggregateDimension(weighted, AudienceDimension.IN_MARKET_USER_INTEREST)
  ]
    .sort((a, b) => b.share - a.share)
    .slice(0, REFERENCE_ROW_LIMIT);

  return {
    memberCount: members.length,
    membersWithAudience: weighted.filter(({ weight }) => weight > 0).length,
    weighting,
    interest: topInterest,
    unavailableInterests,
    gender: suggestGender(aggregateDimension(weighted, AudienceDimension.GENDER)),
    ageRange: suggestAgeRange(aggregateDimension(weighted, AudienceDimension.AGE_RANGE)),
    devices: aggregateDimension(weighted, AudienceDimension.DEVICE).slice(0, REFERENCE_ROW_LIMIT),
    countries: aggregateDimension(weighted, AudienceDimension.GEO_TARGET_COUNTRY).slice(
      0,
      REFERENCE_ROW_LIMIT
    )
  };
};

export const percent = (share: number): string => `${Math.round(share * 100)}%`;

// "Phone 95%, Connected TV 67%"
export const formatShares = (shares: AttributeShare[]): string =>
  shares.map(({ label, share }) => `${label} ${percent(share)}`).join(', ');

// The three copyable Build Recommendation fields as the form displays them, each with its caveat.
export const toCopyableRows = ({
  interest,
  gender,
  ageRange
}: CreatorSetTargeting): CopyableRow[] => [
  {
    label: AttributeField.INTEREST,
    value: interest?.label,
    note: interest && 'The top topic across the set’s viewers'
  },
  {
    label: AttributeField.GENDER,
    value: gender && Gender[gender.key],
    note:
      gender &&
      (gender.isFallback
        ? `No gender reaches ${percent(GENDER_DOMINANCE_MIN)} of viewers with a known gender (${formatShares(gender.shares)})`
        : formatShares(gender.shares))
  },
  {
    label: AttributeField.AGE_RANGE,
    value: ageRange?.keys.map(key => AgeRange[key]).join(', '),
    note:
      ageRange &&
      [
        `Covers ${percent(ageRange.coveredShare)} of viewers with a known age`,
        ...(ageRange.bridgedKeys.length > 0
          ? [
              `includes ${ageRange.bridgedKeys.map(key => AgeRange[key]).join(', ')} to keep the range contiguous`
            ]
          : [])
      ].join('; ')
  }
];

// The suggested Audience Interest, Gender and Age Range as Build Recommendation form state.
export const toBuildRecommendationPrefill = ({
  interest,
  gender,
  ageRange
}: CreatorSetTargeting): BuildRecommendationPrefill => ({
  interest: interest && { id: interest.id, label: interest.label },
  gender: gender?.key,
  ageRange: ageRange?.keys
});

// Tab-separated "label<TAB>value" lines, for pasting the three form values in one go.
export const toTsv = (rows: { label: string; value: string }[]): string =>
  rows.map(({ label, value }) => `${label}\t${value}`).join('\n');
